import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import type { AIAnalysis } from '../../../types/checkin';
import { Card, CardHeader, CardContent } from '../../ui/Card';
import { Badge } from '../../ui/Badge';
import { Button } from '../../ui/Button';
import { SparklesIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import { useAuthStore } from '../../../stores/authStore';
import { getTodayNightlyReview } from '../../../services/firebase/checkin';

// Risk level emojis
const riskEmojis = {
  low: '🌟',
  medium: '⚠️',
  high: '🚨',
  crisis: '🆘',
};

// Risk level badge variants
const riskVariants = {
  low: 'success' as const,
  medium: 'warning' as const,
  high: 'danger' as const,
  crisis: 'danger' as const,
};

export function InsightsWidget() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [analysis, setAnalysis] = useState<AIAnalysis | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function loadTodayInsights() {
      if (!user?.uid) {
        setLoading(false);
        return;
      }

      try {
        const review = await getTodayNightlyReview(user.uid);
        if (review?.aiAnalysis) {
          setAnalysis(review.aiAnalysis);
        }
      } catch (error) {
        console.error('Failed to load today\'s insights:', error);
      } finally {
        setLoading(false);
      }
    }

    loadTodayInsights();
  }, [user?.uid]);

  // Don't render if no insights yet
  if (loading || !analysis) {
    return null;
  }

  const topInsight = analysis.feedback[0];
  const emoji = riskEmojis[analysis.riskLevel];
  const variant = riskVariants[analysis.riskLevel];

  return (
    <Card variant="default" hoverable>
      <CardHeader
        title="Today's Insight"
        action={
          <div className="flex items-center gap-2">
            <span className="text-2xl">{emoji}</span>
            <SparklesIcon className="w-5 h-5 text-accent-500" />
          </div>
        }
      />

      <CardContent>
        <div className="space-y-4">
          {/* Risk Level Badge */}
          <div className="flex items-center gap-2">
            <Badge variant={variant} size="sm">
              {analysis.riskLevel.toUpperCase()}
            </Badge>
            <span className="text-xs text-surface-500 dark:text-surface-400">
              {new Date().toLocaleDateString('en-US', {
                weekday: 'short',
                month: 'short',
                day: 'numeric',
              })}
            </span>
          </div>

          {/* Top Insight */}
          <p className="text-surface-700 dark:text-surface-300 leading-relaxed">
            {topInsight}
          </p>

          {/* View Full Analysis Link */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/journal?view=insights')}
            rightIcon={<ChevronRightIcon className="w-4 h-4" />}
            className="!px-0 !py-0 !h-auto"
          >
            View full analysis
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export default InsightsWidget;
